import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BellRing, Loader2, Calendar, Check } from "lucide-react";
import { actionRemind } from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [
  { id: "tomorrow", label: "Tomorrow", hint: "Same time, one day out" },
  { id: "next_monday", label: "Next Monday", hint: "With your next brief" },
  { id: "one_week", label: "In one week", hint: "Seven days from today" },
  { id: "custom", label: "Pick a date", hint: "You choose" },
];

function isoDatePlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function RemindFlow({ open, onOpenChange, recommendation, onSubmitted }) {
  const [preset, setPreset] = useState("next_monday");
  const [customDate, setCustomDate] = useState(isoDatePlusDays(3));
  const [busy, setBusy] = useState(false);

  if (!recommendation) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { preset };
      if (preset === "custom") payload.customDate = new Date(customDate + "T07:00:00").toISOString();
      const res = await actionRemind(recommendation.id, payload);
      const when = res.remindAt ? new Date(res.remindAt).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "later";
      toast.success("Got it — set aside.", { description: `We'll bring this back on ${when}.` });
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Couldn't set the reminder.", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 rounded-[24px] overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="remind-flow-modal"
      >
        <DialogHeader className="px-6 pt-6">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
              style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
            >
              <BellRing size={22} strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
                Come back to this later.
              </DialogTitle>
              <DialogDescription className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
                We'll tuck this away and bring it back when it makes sense.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pt-4 pb-6 flex flex-col gap-3" data-testid="remind-options">
          {PRESETS.map((p) => {
            const active = preset === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                data-testid={`remind-option-${p.id}`}
                className="w-full text-left rounded-2xl p-4 border-2 flex items-center gap-4 active:scale-[0.99] transition-all"
                style={{
                  background: active ? "var(--ta-primary)" : "#fff",
                  borderColor: active ? "var(--ta-primary)" : "var(--ta-border)",
                  color: active ? "#fff" : "var(--ta-primary)",
                  minHeight: 64,
                }}
              >
                <div
                  className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
                  style={{ background: active ? "rgba(255,255,255,0.15)" : "var(--ta-accent)", color: active ? "#fff" : "var(--ta-primary)" }}
                >
                  <Calendar size={17} strokeWidth={2.2} />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: active ? "rgba(255,255,255,0.75)" : "var(--ta-text-secondary)" }}>
                    {p.hint}
                  </div>
                </div>
                {active && <Check size={20} className="shrink-0" />}
              </button>
            );
          })}

          {preset === "custom" && (
            <input
              type="date"
              value={customDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setCustomDate(e.target.value)}
              data-testid="remind-custom-date"
              className="w-full rounded-2xl bg-white border-2 px-4 py-3.5 text-base outline-none focus:border-[var(--ta-primary)]"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
            />
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              data-testid="remind-cancel"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium border-2"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
            >
              Never mind
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="remind-submit"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "var(--ta-primary)" }}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2 justify-center"><Loader2 size={18} className="animate-spin" /> Setting…</span>
              ) : (
                "Set reminder"
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
